import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/Utils/NewIcon';
import GenericBadge from 'components/Utils/GenericBadge';
import MiscUtils from 'services/MiscUtils';
import './style.scss';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import moment from 'moment-timezone';
import CalendarUtils from 'services/CalendarUtils';
import { GetStaff, IsPhysician } from 'careand-redux/selectors';

/**
 * @param {any} props
 */
const ContactBadge = ({ contact, buttons }) => {
  const dispatch = useAppDispatch();
  const staff = useAppSelector(GetStaff);
  const isPhysician = useAppSelector(IsPhysician);

  const [loading, setLoading] = useState(false);

  const contactId = contact?.id;

  const isFavorite = useMemo(() => {
    return (staff?.favorite_contacts || []).includes(contactId);
  }, [staff, contactId]);

  const isUnavailable = moment.unix(contact.unavailable_until ?? 0).isAfter(moment());

  async function onFavorite(event) {
    event.stopPropagation();
    event.preventDefault();
    setLoading(true);
    let favorite_contacts = staff.favorite_contacts || [];

    if (isFavorite) favorite_contacts = favorite_contacts.filter((id) => id !== contactId);
    else favorite_contacts.push(contactId);

    let updated = {
      ...staff,
      favorite_contacts,
    };
    if (isPhysician) {
      await dispatch(api.Physicians.updatePhysician({ id: staff.id, physician: updated }), {
        errorTitle: 'Favorite Contact',
      });
    } else {
      await dispatch(api.Admins.updateAdmin({ id: staff.id, admin: updated }), {
        errorTitle: 'Favorite Contact',
      });
    }
    setLoading(false);
  }

  return (
    <GenericBadge
      className="contact-badge badge"
      title={
        <>
          {getName(contact)}
          {!!contact.alert && (
            <div style={{ paddingLeft: '1ch' }}>
              <Icon
                src={['fad', 'circle-exclamation']}
                className="red"
                showTooltip
                alt={contact.alert}
                width="15px"
                iconStyle={{
                  '--fa-primary-color': 'white',
                  '--fa-secondary-color': 'red',
                  '--fa-secondary-opacity': 1.0,
                }}
              />
            </div>
          )}
          {isUnavailable && (
            <div style={{ paddingLeft: '1ch' }}>
              <Icon
                src="user-slash"
                showTooltip
                alt={`Not taking patients until ${CalendarUtils.format(contact.unavailable_until)}`}
                width="15px"
              />
            </div>
          )}
        </>
      }
      info={
        <InfoText
          contact={contact}
          isFavorite={isFavorite}
          onFavorite={isPhysician ? onFavorite : undefined}
          loading={loading}
          buttons={buttons}
        />
      }
    />
  );
};

const InfoText = ({ contact, isFavorite, onFavorite, loading, buttons }) => {
  let address = MiscUtils.getFullAddress(contact.address);
  return (
    <>
      {!buttons && !!onFavorite && (
        <div className="favorite-button">
          <Icon
            src="star"
            width="20px"
            height="20px"
            alt={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
            showTooltip={true}
            fontAwesomeStyle={isFavorite ? 'fas' : 'far'}
            onClick={onFavorite}
            loading={loading}
          />
        </div>
      )}
      {buttons}
      <span className="fax-phone">
        <Icon src={['fas', 'phone']} height="12px" />
        {contact.phone}
        <Icon src={['fas', 'fax']} height="12px" />
        {contact.fax}
      </span>
      <br />
      {!contact.email && (
        <span className="address truncate" title={address}>
          <Icon src={['fas', 'location-pin']} height="12px" />
          {address}
        </span>
      )}
      {!!contact.email && (
        <span className="address truncate">
          <Icon src={['fas', 'envelope']} height="12px" />
          {contact.email}
        </span>
      )}
    </>
  );
};

function getName(contact) {
  let name = `${contact.name}`;
  if (contact.specialty) name += ` (${contact.specialty})`;
  return name;
}

ContactBadge.propTypes = {
  pharmacy: PropTypes.object,
};

export default ContactBadge;
